import { v4 as uuidv4 } from 'uuid';

import {
	calculateConsortium,
	calculateFinancing,
	calculateHomeEquity
} from './creditCalculations';
import {
	clampNumber,
	formatBrl,
	getCpfDigits,
	isValidCpf,
	maskCpf,
	parseDigits
} from './creditFormat';
import { CREDIT_CONTACT_STATE_KEY } from './useCreditContactForm';

export type CreditProduct = 'financing' | 'consortium' | 'homeEquity';
export type CreditStep = 0 | 1 | 2 | 3;
export type CreditAnswer = 'intent' | 'income';

export interface CreditInputs {
	financingValue: number;
	financingDownPaymentPercent: number;
	financingYears: number;
	consortiumLetter: number;
	consortiumMonths: number;
	homeEquityValue: number;
	homeEquityPercent: number;
	homeEquityMonths: number;
}

export type CreditInputKey = keyof CreditInputs;

export interface CreditSimulatorState {
	product: CreditProduct;
	step: CreditStep;
	inputs: CreditInputs;
	intent: string | null;
	income: string | null;
	cpf: string;
	hasTriedSubmit: boolean;
	// Ties the Mixpanel events of one filling; created on the first click.
	formHash: string | null;
}

export const CREDIT_INPUT_LIMITS: Record<
	CreditInputKey,
	{ min: number; max: number; step: number }
> = {
	financingValue: { min: 300000, max: 20000000, step: 50000 },
	financingDownPaymentPercent: { min: 10, max: 80, step: 5 },
	financingYears: { min: 5, max: 35, step: 1 },
	consortiumLetter: { min: 100000, max: 3000000, step: 25000 },
	consortiumMonths: { min: 100, max: 240, step: 10 },
	homeEquityValue: { min: 300000, max: 20000000, step: 50000 },
	homeEquityPercent: { min: 10, max: 60, step: 5 },
	homeEquityMonths: { min: 36, max: 240, step: 12 }
};

const LAST_STEP: CreditStep = 3;

function createInitialState(): CreditSimulatorState {
	return {
		product: 'financing',
		step: 0,
		inputs: {
			financingValue: 1200000,
			financingDownPaymentPercent: 30,
			financingYears: 30,
			consortiumLetter: 600000,
			consortiumMonths: 200,
			homeEquityValue: 1500000,
			homeEquityPercent: 40,
			homeEquityMonths: 180
		},
		intent: null,
		income: null,
		cpf: '',
		hasTriedSubmit: false,
		formHash: null
	};
}

function toStep(value: number): CreditStep {
	return clampNumber(value, 0, LAST_STEP) as CreditStep;
}

// Shared by the simulator card and the goal cards, which jump to a product.
export function useCreditSimulator() {
	const state = useState<CreditSimulatorState>(
		'credit-simulator',
		createInitialState
	);

	const financing = computed(() =>
		calculateFinancing({
			propertyValue: state.value.inputs.financingValue,
			downPaymentPercent: state.value.inputs.financingDownPaymentPercent,
			years: state.value.inputs.financingYears
		})
	);
	const consortium = computed(() =>
		calculateConsortium({
			letterValue: state.value.inputs.consortiumLetter,
			months: state.value.inputs.consortiumMonths
		})
	);
	const homeEquity = computed(() =>
		calculateHomeEquity({
			propertyValue: state.value.inputs.homeEquityValue,
			creditPercent: state.value.inputs.homeEquityPercent,
			months: state.value.inputs.homeEquityMonths
		})
	);

	const summaryAmount = computed(() => {
		const amounts: Record<CreditProduct, number> = {
			financing: financing.value.financedAmount,
			consortium: state.value.inputs.consortiumLetter,
			homeEquity: homeEquity.value.credit
		};
		return formatBrl(amounts[state.value.product]);
	});

	// Optional: an empty CPF is fine, a filled one must be valid.
	const isCpfValid = computed(
		() =>
			getCpfDigits(state.value.cpf).length === 0 || isValidCpf(state.value.cpf)
	);
	const hasCpfError = computed(
		() =>
			!isCpfValid.value &&
			(state.value.hasTriedSubmit ||
				getCpfDigits(state.value.cpf).length === 11)
	);

	function next() {
		state.value.step = toStep(state.value.step + 1);
	}

	function back() {
		state.value.step = toStep(state.value.step - 1);
	}

	function restart() {
		Object.assign(state.value, {
			step: 0,
			intent: null,
			income: null,
			cpf: '',
			hasTriedSubmit: false,
			formHash: null
		});
		clearNuxtState(CREDIT_CONTACT_STATE_KEY);
	}

	// Client only: a hash created during SSR would be cached for every visitor.
	function ensureFormHash(): string {
		state.value.formHash ??= uuidv4();
		return state.value.formHash;
	}

	function selectProduct(product: CreditProduct) {
		state.value.product = product;
	}

	function startProduct(product: CreditProduct) {
		state.value.product = product;
		state.value.step = 0;
	}

	function setInput(key: CreditInputKey, value: number) {
		state.value.inputs[key] = value;
	}

	function typeInput(key: CreditInputKey, text: string) {
		setInput(key, parseDigits(text));
	}

	function setPercentFromAmount(
		key: CreditInputKey,
		baseKey: CreditInputKey,
		amount: number
	) {
		const base = state.value.inputs[baseKey] || 0;
		setInput(key, base > 0 ? (amount / base) * 100 : 0);
	}

	function commitInput(key: CreditInputKey) {
		const { min, max } = CREDIT_INPUT_LIMITS[key];
		setInput(key, clampNumber(state.value.inputs[key] || 0, min, max));
	}

	function answer(question: CreditAnswer, option: string) {
		state.value[question] = option;
	}

	function typeCpf(text: string) {
		state.value.cpf = maskCpf(text);
	}

	function flagSubmitAttempt() {
		state.value.hasTriedSubmit = true;
	}

	function submit() {
		if (!isCpfValid.value) {
			state.value.hasTriedSubmit = true;
			return;
		}
		state.value.step = LAST_STEP;
	}

	return {
		state,
		financing,
		consortium,
		homeEquity,
		summaryAmount,
		hasCpfError,
		isCpfValid,
		next,
		back,
		restart,
		ensureFormHash,
		selectProduct,
		startProduct,
		setInput,
		typeInput,
		setPercentFromAmount,
		commitInput,
		answer,
		typeCpf,
		flagSubmitAttempt,
		submit
	};
}
